import { useState, useEffect, useRef } from 'react';
import axios from 'axios';

// Polls the real backend health check (registry/main.py's GET /health) so the
// "All systems operational" badge reflects actual service state instead of
// being a hardcoded always-green label. Re-checks periodically so a backend
// outage that starts after page load is picked up without a refresh.
const POLL_INTERVAL_MS = 30000;
const REQUEST_TIMEOUT_MS = 5000;

export type SystemStatus = 'checking' | 'operational' | 'degraded';

interface HealthResponse {
  status?: string;
}

export function useSystemStatus(): SystemStatus {
  const [status, setStatus] = useState<SystemStatus>('checking');
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    const checkHealth = async () => {
      try {
        const response = await axios.get<HealthResponse>('/health', { timeout: REQUEST_TIMEOUT_MS });
        if (!mountedRef.current) return;
        setStatus(response.data?.status === 'healthy' ? 'operational' : 'degraded');
      } catch {
        if (!mountedRef.current) return;
        setStatus('degraded');
      }
    };

    checkHealth();
    const interval = setInterval(checkHealth, POLL_INTERVAL_MS);

    return () => {
      mountedRef.current = false;
      clearInterval(interval);
    };
  }, []);

  return status;
}
